/**
 * Book a Strategy Call page: landing page with hero, content sections, scheduling form, and secondary nav.
 * Matches HomePage look and feel (typography, SMB 2026 theme, imagery).
 * Saves to Firestore contact_messages only. No client-side email.
 *
 * Security:
 *  - Honeypot: hidden "company_url" field; any non-empty value = bot, silently discarded.
 *  - Time guard: submission rejected if form is completed in under 3 seconds (bot behaviour).
 *  - maxLength on every field to prevent oversized payloads.
 *  - All values are trimmed before Firestore write.
 *  - Once submitted successfully, form lock prevents duplicate writes.
 *  - URL field enforced https/http prefix on submit.
 */

import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
	Loader2,
	ChevronDown,
	ArrowRight,
	CheckCircle2,
	AlertCircle,
	CheckCircle,
} from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, appId } from "@/lib/firebase";

const heroImage = "/assets/images/strategy-meeting.png";

/* ─── Helpers ──────────────────────────────────────────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+\d\s\-().]{7,20}$/;
const URL_RE = /^https?:\/\/.+\..+/i;

function validateName(v: string): string | null {
	const t = v.trim();
	if (!t) return "Name is required.";
	if (t.length < 2) return "Name must be at least 2 characters.";
	return null;
}
function validateEmail(v: string): string | null {
	const t = v.trim();
	if (!t) return "Email address is required.";
	if (!EMAIL_RE.test(t)) return "Please enter a valid email address.";
	return null;
}
function validateCompany(v: string): string | null {
	const t = v.trim();
	if (!t) return "Company name is required.";
	if (t.length < 2) return "Company name must be at least 2 characters.";
	return null;
}
function validatePhone(v: string): string | null {
	const t = v.trim();
	if (!t) return null; // optional
	if (!PHONE_RE.test(t)) return "Please enter a valid phone number.";
	return null;
}
function validateWebsite(v: string): string | null {
	const t = v.trim();
	if (!t) return null; // optional
	if (!URL_RE.test(t))
		return "Please enter a full URL, e.g. https://www.example.com";
	return null;
}
function validateCapabilities(v: string): string | null {
	const t = v.trim();
	if (!t) return "A brief description is required.";
	if (t.length < 20)
		return "Please provide at least 20 characters describing your services.";
	return null;
}

/* ─── Hero decorations (same as HomePage) ───────────────────────────── */

function HeroPattern(): JSX.Element {
	return (
		<svg className="absolute inset-0 z-[3] h-full w-full" aria-hidden="true">
			<defs>
				<pattern
					id="book-call-hero-grid"
					width="40"
					height="40"
					patternUnits="userSpaceOnUse"
				>
					<path
						d="M 40 0 L 0 0 0 40"
						fill="none"
						stroke="rgba(255,255,255,0.06)"
						strokeWidth="0.5"
					/>
				</pattern>
				<linearGradient
					id="book-call-hero-shine"
					x1="0%"
					y1="0%"
					x2="100%"
					y2="100%"
				>
					<stop offset="0%" stopColor="rgba(59,130,246,0.15)" />
					<stop offset="100%" stopColor="transparent" />
				</linearGradient>
			</defs>
			<rect width="100%" height="100%" fill="url(#book-call-hero-grid)" />
			<rect width="100%" height="100%" fill="url(#book-call-hero-shine)" />
		</svg>
	);
}

function HeroIllustration(): JSX.Element {
	return (
		<div
			className="absolute right-0 top-1/2 z-[3] -translate-y-1/2 w-64 sm:w-80 lg:w-96 opacity-20 pointer-events-none hidden lg:block"
			aria-hidden="true"
		>
			<svg viewBox="0 0 400 300" fill="none" className="w-full h-auto">
				<circle
					cx="320"
					cy="80"
					r="120"
					stroke="white"
					strokeWidth="1.5"
					fill="none"
				/>
				<circle
					cx="280"
					cy="160"
					r="80"
					stroke="white"
					strokeWidth="1"
					fill="none"
					opacity="0.7"
				/>
				<path
					d="M120 180 L200 120 L280 180 L360 140"
					stroke="white"
					strokeWidth="2"
					fill="none"
					strokeLinecap="round"
					strokeLinejoin="round"
					opacity="0.8"
				/>
				<circle
					cx="120"
					cy="220"
					r="40"
					stroke="white"
					strokeWidth="1"
					fill="none"
					opacity="0.6"
				/>
			</svg>
		</div>
	);
}

/* ─── Field component (Apple HIG-style) ─────────────────────────────── */

interface FieldProps {
	id: string;
	label: string;
	optional?: boolean;
	error?: string | null;
	touched?: boolean;
	children: React.ReactNode;
}
function Field({
	id,
	label,
	optional,
	error,
	touched,
	children,
}: FieldProps): JSX.Element {
	const hasError = touched && !!error;
	return (
		<div>
			<div className="flex items-baseline justify-between mb-1.5">
				<label
					htmlFor={id}
					className="block text-sm font-semibold text-smb-slate-800"
				>
					{label}
					{!optional && (
						<span className="text-[#224896] ml-0.5" aria-hidden="true">
							*
						</span>
					)}
				</label>
				{optional && (
					<span className="text-xs text-smb-slate-400 font-normal">
						Optional
					</span>
				)}
			</div>
			{children}
			{hasError && (
				<p
					id={`${id}-error`}
					role="alert"
					className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
				>
					<AlertCircle size={13} aria-hidden="true" className="flex-shrink-0" />
					{error}
				</p>
			)}
		</div>
	);
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export interface BookStrategyCallPageProps {
	calendarEmbedUrl?: string | null;
}

type FieldKey =
	| "fullName"
	| "email"
	| "companyName"
	| "phone"
	| "website"
	| "capabilities";

export function BookStrategyCallPage({
	calendarEmbedUrl,
}: BookStrategyCallPageProps): JSX.Element {
	const navigate = useNavigate();

	/* Form values */
	const [fullName, setFullName] = useState("");
	const [email, setEmail] = useState("");
	const [companyName, setCompanyName] = useState("");
	const [phone, setPhone] = useState("");
	const [website, setWebsite] = useState("");
	const [capabilities, setCapabilities] = useState("");

	/* Honeypot — bots fill this; humans never see it */
	const [honeypot, setHoneypot] = useState("");

	/* Track which fields the user has touched (for live validation) */
	const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>(
		{},
	);

	/* Submission state */
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false); // lock after success
	const [submitError, setSubmitError] = useState<string | null>(null);

	/* Time guard: record when the user first interacts with the form */
	const formStartRef = useRef<number | null>(null);
	const startTimer = () => {
		if (!formStartRef.current) formStartRef.current = Date.now();
	};

	/* Per-field errors */
	const errors: Partial<Record<FieldKey, string | null>> = {
		fullName: validateName(fullName),
		email: validateEmail(email),
		companyName: validateCompany(companyName),
		phone: validatePhone(phone),
		website: validateWebsite(website),
		capabilities: validateCapabilities(capabilities),
	};

	const touch = (field: FieldKey) =>
		setTouched((p) => ({ ...p, [field]: true }));

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		/* Touch all fields to reveal any remaining errors */
		setTouched({
			fullName: true,
			email: true,
			companyName: true,
			phone: true,
			website: true,
			capabilities: true,
		});

		/* Anti-spam: honeypot */
		if (honeypot.trim()) {
			/* Silently succeed to not reveal detection */
			setSubmitted(true);
			return;
		}

		/* Anti-spam: time guard — bots fill forms in < 2 seconds */
		const elapsed = formStartRef.current
			? Date.now() - formStartRef.current
			: Infinity;
		if (elapsed < 2000) {
			setSubmitted(true);
			return;
		}

		/* Client-side validation gate */
		const hasErrors = Object.values(errors).some(Boolean);
		if (hasErrors) return;

		/* Duplicate submission lock */
		if (submitted || submitting) return;

		setSubmitting(true);
		setSubmitError(null);

		const fullNameTrim = fullName.trim();
		const emailTrim = email.trim().toLowerCase();
		const companyTrim = companyName.trim();
		const phoneTrim = phone.trim() || null;
		const websiteTrim = website.trim() || null;
		const capabilitiesTrim = capabilities.trim();

		try {
			const collRef = collection(
				db,
				"artifacts",
				appId,
				"public",
				"data",
				"contact_messages",
			);
			await addDoc(collRef, {
				fullName: fullNameTrim,
				email: emailTrim,
				companyName: companyTrim,
				phone: phoneTrim,
				website: websiteTrim,
				capabilities: capabilitiesTrim,
				source: "strategy_call",
				adminStatus: "new",
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
			});
			setSubmitted(true);
		} catch (err) {
			console.error(
				"[BookStrategyCallPage] Failed to save strategy call submission:",
				err,
			);
			setSubmitError(
				"Something went wrong. Please try again or contact us directly.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	const scrollToScheduling = () => {
		document
			.getElementById("scheduling")
			?.scrollIntoView({ behavior: "smooth" });
	};

	/* Apple HIG: clean, full-radius, clear focus ring, subtle border */
	const baseInputClass =
		"w-full rounded-xl border bg-white px-4 py-3 text-[15px] text-smb-ink leading-snug transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0";
	const inputClass = (field: FieldKey) => {
		const isError = touched[field] && !!errors[field];
		return `${baseInputClass} ${
			isError
				? "border-red-400 focus:ring-red-400/40 focus:border-red-500"
				: "border-[#d1d5db] focus:ring-[#224896]/40 focus:border-[#224896]"
		}`;
	};

	return (
		<main
			id="main-content"
			className="font-sans text-gray-900"
			role="main"
			aria-label="Book a Strategy Call"
		>
			{/* ── Hero ─────────────────────────────────────────────────────── */}
			<section
				className="relative text-white overflow-hidden min-h-[28rem] flex items-center"
				style={{ backgroundColor: "var(--smb-primary)" }}
			>
				<div
					className="absolute inset-0 z-0 w-full h-full bg-cover bg-center bg-no-repeat"
					style={{ backgroundImage: `url(${heroImage})` }}
					aria-hidden="true"
				/>
				<div
					className="absolute inset-0 z-[1] w-full h-full pointer-events-none"
					style={{
						background:
							"linear-gradient(to right, rgba(30,58,95,0.92) 0%, rgba(30,58,95,0.75) 40%, rgba(15,23,42,0.7) 100%)",
					}}
					aria-hidden="true"
				/>

				{/* Decorative SVG (lines/grid only; no solid fill so hero image shows through) */}
				<svg
					viewBox="0 0 1600 700"
					preserveAspectRatio="none"
					xmlns="http://www.w3.org/2000/svg"
					className="pointer-events-none absolute inset-0 z-[2] h-full w-full"
					aria-hidden="true"
				>
					<defs>
						<pattern
							id="hero-grid-lines"
							width="60"
							height="60"
							patternUnits="userSpaceOnUse"
						>
							<path
								d="M60 0 L0 0 0 60"
								fill="none"
								stroke="#60a5fa"
								strokeOpacity="0.06"
							/>
						</pattern>

						<filter id="hero-glow">
							<feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
							<feMerge>
								<feMergeNode in="coloredBlur" />
								<feMergeNode in="SourceGraphic" />
							</feMerge>
						</filter>

					</defs>

					<rect width="100%" height="100%" fill="url(#hero-grid-lines)" />

					<g stroke="#60a5fa" strokeOpacity="0.18" strokeWidth="1.5">
						<line x1="100" y1="120" x2="420" y2="210" />
						<line x1="420" y1="210" x2="720" y2="150" />
						<line x1="720" y1="150" x2="1050" y2="240" />
						<line x1="1050" y1="240" x2="1400" y2="180" />

						<line x1="300" y1="420" x2="600" y2="320" />
						<line x1="600" y1="320" x2="920" y2="420" />
						<line x1="920" y1="420" x2="1300" y2="350" />

						<line x1="420" y1="210" x2="600" y2="320" />
						<line x1="720" y1="150" x2="920" y2="420" />
					</g>

					<circle r="4" fill="#93c5fd" filter="url(#hero-glow)">
						<animateMotion
							dur="6s"
							repeatCount="indefinite"
							path="M100 120 L420 210 L720 150 L1050 240 L1400 180"
						/>
					</circle>

					<circle r="4" fill="#93c5fd" filter="url(#hero-glow)">
						<animateMotion
							dur="7s"
							repeatCount="indefinite"
							path="M300 420 L600 320 L920 420 L1300 350"
						/>
					</circle>

					<g fill="#bfdbfe" filter="url(#hero-glow)">
						<circle cx="100" cy="120" r="3" />
						<circle cx="420" cy="210" r="4" />
						<circle cx="720" cy="150" r="3" />
						<circle cx="1050" cy="240" r="4" />
						<circle cx="1400" cy="180" r="3" />

						<circle cx="300" cy="420" r="3" />
						<circle cx="600" cy="320" r="4" />
						<circle cx="920" cy="420" r="3" />
						<circle cx="1300" cy="350" r="4" />
					</g>

					<g fill="#60a5fa" fillOpacity="0.15">
						<circle cx="250" cy="260" r="2" />
						<circle cx="560" cy="150" r="2" />
						<circle cx="880" cy="300" r="2" />
						<circle cx="1200" cy="150" r="2" />
						<circle cx="1500" cy="420" r="2" />
					</g>
				</svg>
				<HeroPattern />
				<HeroIllustration />
				<div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center lg:text-left">
					<div className="max-w-3xl">
						<h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
							Schedule a Strategy Call
						</h1>
						<p className="mt-4 text-xl sm:text-2xl font-medium text-white/95">
							If your company is interested in building stronger relationships
							with CKD patients and caregivers, a short strategy call can help determine
							whether GSGO may be able to support your goals.
						</p>
						<p className="mt-6 text-base sm:text-lg leading-relaxed text-white/90">
							During this conversation, we will discuss your current
							current approach and explore whether a structured
							engagement process could help create more consistent impact
							for families and communities.
						</p>
						<div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
							<button
								type="button"
								onClick={scrollToScheduling}
								className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#1e3a5f] bg-white text-[#1e3a5f] hover:bg-gray-100"
							>
								Schedule Your Call
								<ChevronDown size={18} aria-hidden="true" />
							</button>
							<Link
								to="/how-it-works"
								className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-white text-white font-semibold hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#1e3a5f]"
							>
								See How It Works
							</Link>
						</div>
					</div>
				</div>
			</section>

			{/* ── Two-column layout: left = scrollable content, right = sticky calendar + form ── */}
			<div className="lg:grid lg:grid-cols-[1fr,minmax(520px,600px)] lg:items-start">
				{/* ── LEFT COLUMN: scrollable content sections ── */}
				<div className="min-w-0">
					{/* Who This Call Is Designed For */}
					<section className="relative overflow-hidden bg-white border-b border-smb-slate-100">
						<div className="relative z-10 px-6 sm:px-10 py-16 sm:py-20 lg:py-24">
							<p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
								Who This Call Is For
							</p>
							<h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
								Who This Call Is Designed For
							</h2>
							<p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
								This call is typically most helpful for individuals or organizations that:
							</p>
							<div className="mt-8 relative overflow-hidden rounded-2xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 sm:p-6 shadow-md shadow-smb-primary/10">
								<div
									className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl"
									aria-hidden="true"
								/>
								<div className="relative">
									<ul className="space-y-3 text-smb-slate-800">
										{[
											"already participate in federal contracting",
											"have past performance or contract experience",
											"are interested in expanding community support relationships",
											"want a more structured approach to Prime engagement",
										].map((item) => (
											<li key={item} className="flex items-start gap-2.5">
												<CheckCircle2
													size={20}
													className="text-[#224896] flex-shrink-0 mt-0.5"
													aria-hidden="true"
												/>
												{item}
											</li>
										))}
									</ul>
								</div>
							</div>
							<p className="mt-6 text-lg text-smb-slate-600 leading-relaxed max-w-2xl">
								If your company is exploring ways to build stronger
								relationships with partners and supporters, this call can help clarify
								the next steps.
							</p>
						</div>
					</section>

					{/* What We Will Discuss */}
					<section className="bg-smb-slate-100/50 border-b border-smb-slate-100">
						<div className="px-6 sm:px-10 py-16 sm:py-20 lg:py-24">
							<p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
								Topics
							</p>
							<h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
								What We Will Discuss
							</h2>
							<p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
								The strategy call is a short, focused conversation designed to
								better understand your business and goals.
							</p>
							<div className="mt-8 relative overflow-hidden rounded-2xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 sm:p-6 shadow-md shadow-smb-primary/10">
								<div
									className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl"
									aria-hidden="true"
								/>
								<div className="relative">
									<p className="text-sm font-semibold uppercase tracking-wide text-smb-slate-600 mb-3">
										Topics may include:
									</p>
									<ul className="space-y-2.5 text-smb-slate-800">
										{[
											"your current support approach",
											"the types of contracts or agencies you support",
											"potential Prime Contractor alignment",
											"how GSGO's engagement process works",
										].map((item) => (
											<li key={item} className="flex items-start gap-2.5">
												<CheckCircle2
													size={20}
													className="text-[#224896] flex-shrink-0 mt-0.5"
													aria-hidden="true"
												/>
												{item}
											</li>
										))}
									</ul>
								</div>
							</div>
							<div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
								<p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
									The objective is to determine whether GSGO's structured
									approach could be helpful for your goals.
								</p>
							</div>
						</div>
					</section>

					{/* What Happens After the Call */}
					<section className="bg-white border-b border-smb-slate-100">
						<div className="px-6 sm:px-10 py-16 sm:py-20 lg:py-24">
							<p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
								Next Steps
							</p>
							<h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
								What Happens After the Call
							</h2>
							<p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
								At the end of the conversation, one of two things typically
								happens.
							</p>
							<p className="mt-4 text-smb-slate-600 leading-relaxed max-w-2xl">
								If there appears to be a strong fit, we can discuss what the
								next steps might look like for working together.
							</p>
							<p className="mt-4 text-smb-slate-600 leading-relaxed max-w-2xl">
								If GSGO does not appear to be the right solution, we will do
								our best to provide helpful guidance so that you can continue
								pursuing support opportunities effectively.
							</p>
							<div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
								<p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
									Our goal is to ensure the conversation is valuable regardless
									of the outcome.
								</p>
							</div>
						</div>
					</section>

					{/* Schedule intro — anchor for scroll */}
					<section
						id="scheduling"
						className="bg-smb-slate-100/50 border-b border-smb-slate-100 scroll-mt-20"
					>
						<div className="px-6 sm:px-10 py-16 sm:py-20 lg:py-24">
							<p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
								Book Your Slot
							</p>
							<h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
								Schedule Your Conversation
							</h2>
							<p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
								Select a convenient time using the calendar. The call typically
								lasts 20–30 minutes and is conducted virtually.
							</p>

							{/* On mobile/tablet: show calendar + form inline here */}
							<div className="lg:hidden mt-10 space-y-6">
								{calendarEmbedUrl && calendarEmbedUrl.trim() ? (
									<div className="rounded-2xl border border-smb-slate-100 overflow-hidden shadow-sm">
										<iframe
											src={calendarEmbedUrl.trim()}
											title="Schedule Strategy Call"
											className="w-full h-[360px] border-0"
										/>
									</div>
								) : (
									<div className="rounded-2xl border border-smb-slate-100 bg-white min-h-[120px] flex items-center justify-center shadow-sm">
										<p className="text-sm text-smb-slate-400 px-4 text-center">
											A calendar will be displayed here for scheduling.
										</p>
									</div>
								)}
								{submitted ? (
									<div className="rounded-2xl bg-white border border-[#224896]/20 shadow-md p-8 text-center">
										<div className="flex justify-center mb-4">
											<div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
												<CheckCircle
													size={28}
													className="text-green-600"
													aria-hidden="true"
												/>
											</div>
										</div>
										<h3 className="text-xl font-bold text-smb-ink mb-2">
											Request Received
										</h3>
										<p className="text-smb-slate-600 text-[15px] leading-relaxed">
											Thank you. We'll review your information and follow up
											shortly to schedule your strategy call.
										</p>
										<Link
											to="/"
											className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#224896] hover:text-[#15285f] transition-colors"
										>
											Return to Homepage{" "}
											<ArrowRight size={16} aria-hidden="true" />
										</Link>
									</div>
								) : (
									<div className="rounded-2xl bg-white shadow-[0_2px_24px_-4px_rgba(21,40,95,0.12),0_0_0_1px_rgba(21,40,95,0.06)] overflow-hidden">
										<div className="px-6 pt-6 pb-4 border-b border-[#f1f5f9]">
											<h3 className="text-lg font-bold text-smb-ink">
												Request a Strategy Call
											</h3>
											<p className="mt-1 text-sm text-smb-slate-500">
												All fields marked with{" "}
												<span className="text-[#224896] font-semibold">*</span>{" "}
												are required.
											</p>
										</div>
										<form
											onSubmit={handleSubmit}
											noValidate
											aria-label="Strategy call request form (mobile)"
											className="px-6 py-5 space-y-5"
										>
											<div
												aria-hidden="true"
												className="absolute opacity-0 pointer-events-none h-0 overflow-hidden"
												tabIndex={-1}
											>
												<label htmlFor="company_url_m">Company URL</label>
												<input
													id="company_url_m"
													name="company_url"
													type="text"
													autoComplete="off"
													tabIndex={-1}
													value={honeypot}
													onChange={(e) => setHoneypot(e.target.value)}
												/>
											</div>
											<Field
												id="m-fullName"
												label="Name"
												error={errors.fullName}
												touched={touched.fullName}
											>
												<input
													id="m-fullName"
													type="text"
													autoComplete="name"
													required
													maxLength={100}
													value={fullName}
													onChange={(e) => {
														setFullName(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("fullName")}
													aria-invalid={!!(touched.fullName && errors.fullName)}
													className={inputClass("fullName")}
													placeholder="Jane Smith"
												/>
											</Field>
											<Field
												id="m-email"
												label="Email Address"
												error={errors.email}
												touched={touched.email}
											>
												<input
													id="m-email"
													type="email"
													autoComplete="email"
													required
													maxLength={254}
													value={email}
													onChange={(e) => {
														setEmail(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("email")}
													aria-invalid={!!(touched.email && errors.email)}
													className={inputClass("email")}
													placeholder="name@company.com"
												/>
											</Field>
											<Field
												id="m-company"
												label="Company Name"
												error={errors.companyName}
												touched={touched.companyName}
											>
												<input
													id="m-company"
													type="text"
													autoComplete="organization"
													required
													maxLength={200}
													value={companyName}
													onChange={(e) => {
														setCompanyName(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("companyName")}
													aria-invalid={
														!!(touched.companyName && errors.companyName)
													}
													className={inputClass("companyName")}
													placeholder="Acme Federal Solutions"
												/>
											</Field>
											<Field
												id="m-phone"
												label="Phone Number"
												optional
												error={errors.phone}
												touched={touched.phone}
											>
												<input
													id="m-phone"
													type="tel"
													autoComplete="tel"
													maxLength={20}
													value={phone}
													onChange={(e) => {
														setPhone(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("phone")}
													aria-invalid={!!(touched.phone && errors.phone)}
													className={inputClass("phone")}
													placeholder="+1 (555) 123-4567"
												/>
											</Field>
											<Field
												id="m-website"
												label="Company Website"
												optional
												error={errors.website}
												touched={touched.website}
											>
												<input
													id="m-website"
													type="url"
													autoComplete="url"
													maxLength={300}
													value={website}
													onChange={(e) => {
														setWebsite(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("website")}
													aria-invalid={!!(touched.website && errors.website)}
													className={inputClass("website")}
													placeholder="https://www.example.com"
												/>
											</Field>
											<Field
												id="m-capabilities"
												label="Brief description of your services or capabilities"
												error={errors.capabilities}
												touched={touched.capabilities}
											>
												<textarea
													id="m-capabilities"
													rows={4}
													required
													maxLength={2000}
													value={capabilities}
													onChange={(e) => {
														setCapabilities(e.target.value);
														startTimer();
													}}
													onBlur={() => touch("capabilities")}
													aria-invalid={
														!!(touched.capabilities && errors.capabilities)
													}
													className={`${inputClass("capabilities")} resize-none`}
													placeholder="Describe your company's services, past performance, or areas of federal contracting interest..."
												/>
												<p className="mt-1 text-xs text-smb-slate-400 text-right tabular-nums">
													{capabilities.length} / 2000
												</p>
											</Field>
											{submitError && (
												<p
													role="alert"
													className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
												>
													<AlertCircle
														size={16}
														className="flex-shrink-0 mt-0.5"
														aria-hidden="true"
													/>
													{submitError}
												</p>
											)}
											<div className="pt-2 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 border-t border-[#f1f5f9]">
												<button
													type="button"
													onClick={() => navigate(-1)}
													disabled={submitting}
													className="w-full sm:w-auto rounded-xl border-2 border-smb-slate-100 bg-white px-5 py-2.5 text-sm font-semibold text-smb-slate-700 hover:bg-smb-slate-100/80 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-slate-400 focus:ring-offset-2"
												>
													Cancel
												</button>
												<button
													type="submit"
													disabled={submitting}
													className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-[#15285f] hover:bg-[#224896] active:scale-[0.98] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#224896] focus:ring-offset-2 shadow-sm"
												>
													{submitting ? (
														<>
															<Loader2
																size={16}
																className="animate-spin"
																aria-hidden="true"
															/>
															Submitting…
														</>
													) : (
														<>
															Request Strategy Call
															<ArrowRight size={16} aria-hidden="true" />
														</>
													)}
												</button>
											</div>
											<p className="text-xs text-smb-slate-400 text-center pt-1">
												Your information is kept confidential and used only to
												prepare for your strategy call.
											</p>
										</form>
									</div>
								)}
							</div>
						</div>
					</section>

					{/* Want to Learn More Before Booking? */}
					<section className="bg-white border-t border-smb-slate-100">
						<div className="px-6 sm:px-10 py-16 sm:py-20">
							<p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
								Explore First
							</p>
							<h2 className="mt-2 text-2xl sm:text-3xl font-bold text-smb-ink">
								Want to Learn More Before Booking?
							</h2>
							<p className="mt-4 text-smb-slate-600 leading-relaxed max-w-2xl">
								If you would like to understand the GSGO process in more
								detail before scheduling a call, you may find the following
								pages helpful.
							</p>
							<div className="mt-8 flex flex-col sm:flex-row gap-4">
								<Link
									to="/how-it-works"
									className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-[#224896] text-[#224896] font-semibold hover:bg-[#224896]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#224896] focus:ring-offset-2"
								>
									See How It Works
									<ArrowRight size={18} aria-hidden="true" />
								</Link>
								<Link
									to="/"
									className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-smb-slate-100 text-smb-slate-800 font-semibold hover:bg-smb-slate-100/80 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-slate-400 focus:ring-offset-2"
								>
									Return to Homepage
								</Link>
							</div>
						</div>
					</section>
				</div>
				{/* end left column */}

				{/* ── RIGHT COLUMN: sticky calendar + form (desktop/tablet only) ── */}
				<div
					className="hidden lg:block lg:sticky lg:top-[72px] lg:self-start lg:h-[calc(100vh-72px)] lg:overflow-y-auto"
					style={{
						background:
							"linear-gradient(160deg, #0f1f4a 0%, #1a3270 55%, #15285f 100%)",
						boxShadow: "-6px 0 32px rgba(15,31,74,0.18)",
					}}
				>
					<div className="p-7 xl:p-9 space-y-6">
						{/* Header */}
						<div className="pb-5 border-b border-white/15">
							<p className="text-xs font-semibold tracking-widest uppercase text-[#7ba3ff]">
								Book Your Slot
							</p>
							<h3 className="mt-1.5 text-2xl font-bold text-white">
								Schedule Your Conversation
							</h3>
							<p className="mt-1 text-sm text-white/60">
								20–30 minutes · Virtual · No obligation
							</p>
						</div>

						{/* Calendar embed */}
						{calendarEmbedUrl && calendarEmbedUrl.trim() ? (
							<div className="rounded-2xl overflow-hidden shadow-lg border border-white/10">
								<iframe
									src={calendarEmbedUrl.trim()}
									title="Schedule Strategy Call"
									className="w-full h-[280px] border-0"
								/>
							</div>
						) : (
							<div className="rounded-2xl border border-white/15 bg-white/[0.08] h-[110px] flex items-center justify-center">
								<p className="text-sm text-white/50 px-4 text-center">
									A calendar will be displayed here for scheduling.
								</p>
							</div>
						)}

						{/* Divider */}
						<div className="flex items-center gap-3">
							<div className="flex-1 h-px bg-white/15" />
							<span className="text-xs font-semibold tracking-wide uppercase text-white/40">
								Your Details
							</span>
							<div className="flex-1 h-px bg-white/15" />
						</div>

						{/* Form */}
						{submitted ? (
							<div className="rounded-2xl bg-white/10 border border-white/20 p-8 text-center">
								<div className="flex justify-center mb-4">
									<div className="w-14 h-14 rounded-full bg-green-400/20 border border-green-400/40 flex items-center justify-center">
										<CheckCircle
											size={28}
											className="text-green-400"
											aria-hidden="true"
										/>
									</div>
								</div>
								<h3 className="text-xl font-bold text-white mb-2">
									Request Received
								</h3>
								<p className="text-white/70 text-[15px] leading-relaxed">
									Thank you. We'll review your information and follow up shortly
									to schedule your strategy call.
								</p>
								<Link
									to="/"
									className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7ba3ff] hover:text-white transition-colors"
								>
									Return to Homepage <ArrowRight size={16} aria-hidden="true" />
								</Link>
							</div>
						) : (
							<div className="rounded-2xl bg-white shadow-[0_8px_40px_rgba(0,0,0,0.3)] overflow-hidden">
								<div className="px-6 pt-6 pb-4 border-b border-[#f1f5f9]">
									<h3 className="text-base font-bold text-smb-ink">
										Request a Strategy Call
									</h3>
									<p className="mt-1 text-xs text-smb-slate-500">
										Fields marked{" "}
										<span className="text-[#224896] font-semibold">*</span> are
										required.
									</p>
								</div>

								<form
									onSubmit={handleSubmit}
									noValidate
									aria-label="Strategy call request form"
									className="px-6 py-5 space-y-4"
								>
									{/* Honeypot */}
									<div
										aria-hidden="true"
										className="absolute opacity-0 pointer-events-none h-0 overflow-hidden"
										tabIndex={-1}
									>
										<label htmlFor="company_url">Company URL</label>
										<input
											id="company_url"
											name="company_url"
											type="text"
											autoComplete="off"
											tabIndex={-1}
											value={honeypot}
											onChange={(e) => setHoneypot(e.target.value)}
										/>
									</div>

									<Field
										id="strategy-fullName"
										label="Name"
										error={errors.fullName}
										touched={touched.fullName}
									>
										<input
											id="strategy-fullName"
											type="text"
											autoComplete="name"
											required
											maxLength={100}
											value={fullName}
											onChange={(e) => {
												setFullName(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("fullName")}
											aria-describedby={
												touched.fullName && errors.fullName
													? "strategy-fullName-error"
													: undefined
											}
											aria-invalid={!!(touched.fullName && errors.fullName)}
											className={inputClass("fullName")}
											placeholder="Jane Smith"
										/>
									</Field>

									<Field
										id="strategy-email"
										label="Email Address"
										error={errors.email}
										touched={touched.email}
									>
										<input
											id="strategy-email"
											type="email"
											autoComplete="email"
											required
											maxLength={254}
											value={email}
											onChange={(e) => {
												setEmail(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("email")}
											aria-describedby={
												touched.email && errors.email
													? "strategy-email-error"
													: undefined
											}
											aria-invalid={!!(touched.email && errors.email)}
											className={inputClass("email")}
											placeholder="name@company.com"
										/>
									</Field>

									<Field
										id="strategy-company"
										label="Company Name"
										error={errors.companyName}
										touched={touched.companyName}
									>
										<input
											id="strategy-company"
											type="text"
											autoComplete="organization"
											required
											maxLength={200}
											value={companyName}
											onChange={(e) => {
												setCompanyName(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("companyName")}
											aria-describedby={
												touched.companyName && errors.companyName
													? "strategy-company-error"
													: undefined
											}
											aria-invalid={
												!!(touched.companyName && errors.companyName)
											}
											className={inputClass("companyName")}
											placeholder="Acme Federal Solutions"
										/>
									</Field>

									<Field
										id="strategy-phone"
										label="Phone Number"
										optional
										error={errors.phone}
										touched={touched.phone}
									>
										<input
											id="strategy-phone"
											type="tel"
											autoComplete="tel"
											maxLength={20}
											value={phone}
											onChange={(e) => {
												setPhone(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("phone")}
											aria-describedby={
												touched.phone && errors.phone
													? "strategy-phone-error"
													: undefined
											}
											aria-invalid={!!(touched.phone && errors.phone)}
											className={inputClass("phone")}
											placeholder="+1 (555) 123-4567"
										/>
									</Field>

									<Field
										id="strategy-website"
										label="Company Website"
										optional
										error={errors.website}
										touched={touched.website}
									>
										<input
											id="strategy-website"
											type="url"
											autoComplete="url"
											maxLength={300}
											value={website}
											onChange={(e) => {
												setWebsite(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("website")}
											aria-describedby={
												touched.website && errors.website
													? "strategy-website-error"
													: undefined
											}
											aria-invalid={!!(touched.website && errors.website)}
											className={inputClass("website")}
											placeholder="https://www.example.com"
										/>
									</Field>

									<Field
										id="strategy-capabilities"
										label="Brief description of your services or capabilities"
										error={errors.capabilities}
										touched={touched.capabilities}
									>
										<textarea
											id="strategy-capabilities"
											rows={3}
											required
											maxLength={2000}
											value={capabilities}
											onChange={(e) => {
												setCapabilities(e.target.value);
												startTimer();
											}}
											onBlur={() => touch("capabilities")}
											aria-describedby={
												touched.capabilities && errors.capabilities
													? "strategy-capabilities-error"
													: undefined
											}
											aria-invalid={
												!!(touched.capabilities && errors.capabilities)
											}
											className={`${inputClass("capabilities")} resize-none`}
											placeholder="Describe your company's services, past performance, or areas of federal contracting interest..."
										/>
										<p className="mt-1 text-xs text-smb-slate-400 text-right tabular-nums">
											{capabilities.length} / 2000
										</p>
									</Field>

									{submitError && (
										<p
											role="alert"
											className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
										>
											<AlertCircle
												size={16}
												className="flex-shrink-0 mt-0.5"
												aria-hidden="true"
											/>
											{submitError}
										</p>
									)}

									<div className="pt-3 space-y-2.5 border-t border-[#f1f5f9]">
										<button
											type="submit"
											disabled={submitting}
											className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#15285f] hover:bg-[#224896] active:scale-[0.98] px-6 py-3 text-sm font-semibold text-white disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#224896] focus:ring-offset-2 shadow-sm"
										>
											{submitting ? (
												<>
													<Loader2
														size={16}
														className="animate-spin"
														aria-hidden="true"
													/>
													Submitting…
												</>
											) : (
												<>
													Request Strategy Call
													<ArrowRight size={16} aria-hidden="true" />
												</>
											)}
										</button>
										<button
											type="button"
											onClick={() => navigate(-1)}
											disabled={submitting}
											className="w-full rounded-xl border border-smb-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-smb-slate-600 hover:bg-smb-slate-50 disabled:opacity-50 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-slate-400 focus:ring-offset-2"
										>
											Cancel
										</button>
									</div>

									<p className="text-xs text-smb-slate-400 text-center pt-1">
										Your information is kept confidential and used only to
										prepare for your strategy call.
									</p>
								</form>
							</div>
						)}
					</div>
				</div>
				{/* end right column */}
			</div>
			{/* end two-column grid */}
		</main>
	);
}
